import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, CardBody, Col, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { queryKeyes } from 'shared/queryKeys';
import { RootState } from 'index';
import { useQuery } from 'react-query';
import { apiMedia } from 'global/api.global';
import { queryClient } from 'react-query/queryClient';
import { errorToast, successToast } from 'Components/Toasts';
import ReportingFileUpload from 'Components/ReportingFileUpload';
import { checkInvalidPrimaryKey, dateTimeFormat } from 'GenericForms/Helper';
import Loading from 'Components/Loading';
import { GetPageWithID } from '../pendingpage.hook';
import PageNavButton from '../PageNavButton';
import { FileStatus } from 'shared/constants';
import Message from 'Components/Message';

/**
 * Component for uploading pending Bill of Lading (BL) files.
 * Handles fetching pending BL files, displaying them in a table,
 * and providing a modal for file upload with validation and API integration.
 */
export const PendingBlAttachment = () => {
    /** State variables */
    const { VesselID } = useSelector((state: RootState) => state.Reporting);
    const [modalState, setModalState] = useState(false);
    const [fileObject, setFileObject] = useState<any>({});
    const [BLFiles, setBLFiles] = useState<Array<any>>([]);
    const [pageUrl, setPageUrl] = useState("");
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [emptyFileError, setEmptyFileError] = useState(false);
    const [cachedReports, setCachedReports] = useState<any>(null);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [uploading, setUploading] = useState(false);
    /** State variables end */


    /**
     * Updates the page URL for fetching pending BL files when VesselID changes.
     */
    useEffect(() => {
        if (!checkInvalidPrimaryKey(VesselID)) {
            return;
        }
        setPageUrl(queryKeyes.pagination.PendingBLattachmentFilesPage.url(VesselID, FileStatus.PENDING));
    }, [VesselID]);

    /** Fetch pending BL files */
    const { data: fileUploadingObj, isLoading: fileUploadingLoading } = useQuery(
        [queryKeyes.pagination.PendingBLattachmentFilesPage.key, VesselID, pageUrl],
        async () => await GetPageWithID(VesselID, pageUrl),
        {
            enabled: true,
            onSuccess: (data) => {
                setInitialLoadComplete(true);
                if (data?.results?.length > 0) {
                    setCachedReports(data);
                }
            }
        }
    );
    const displayData = fileUploadingObj || cachedReports;
    const page = displayData?.results;

    /**
     * Adds CSS class to body to remove padding when modal is open.
     */
    function removeBodyCss() {
        document.body.classList.add("no_padding");
    }

    /**
     * Toggles the modal open/close state and applies body CSS.
     */
    function tog_backdrop() {
        setModalState(!modalState);
        removeBodyCss();
    }

    const handleUploadButtonClick = (file: any) => {
        setModalState(!modalState);
        setFileObject(file);
    };

    const handleUpload = async () => {
        setUploading(true);
        const validFiles = BLFiles.filter(file => file.file !== null && file.file !== undefined);

        if (validFiles.length === 0) {
            setEmptyFileError(true);
            setUploading(false);
            return;
        }

        if (fileObject && BLFiles && BLFiles.length > 0) {
            try {
                const finalObj = new FormData();
                finalObj.append("bill_of_lading", String(fileObject.id));
                BLFiles.forEach((file: any) => {
                    if (file.file !== null && file.file !== undefined) {
                        finalObj.append("bl_figure_file", file.file);
                    }
                });
                const res = await apiMedia.post(`bill_of_lading_bl_figure_file/upload_files/`, finalObj);
                if (res.status === 200 || res.status === 201) {
                    queryClient.invalidateQueries(queryKeyes.pagination.PendingBLattachmentFilesPage.key);
                    queryClient.invalidateQueries(queryKeyes.vessel.BillOfLadingObject.key);
                    queryClient.invalidateQueries(queryKeyes.vessel.ViewFilesByReport.key);
                    queryClient.invalidateQueries(queryKeyes.vessel.ViewFilesByVessel.key); setRefreshKey(refreshKey + 1);
                    setModalState(false);
                    setBLFiles([]);
                    successToast("BL File uploaded successfully!");
                } else {
                    errorToast("Unexpected response, please try again.");
                }
            } catch (err: any) {
                errorToast(err?.response?.data?.detail || "Something went wrong.");
            } finally {
                setUploading(false);
            }
        }
    };
    const showLoadingIndicator = fileUploadingLoading && !initialLoadComplete;
    return (
        <React.Fragment>
            <Row>
                <Col lg={4}>
                    {showLoadingIndicator && <Loading message='Loading required data!' />}
                </Col>
            </Row>
            <CardBody className="px-0 py-0 pb-0 mt-2">
                <div className="table-responsive table-height-10">
                    <table className="table mb-0" key={refreshKey}>
                        <thead className="table-light">
                            <tr>
                                <th className="p-2 align-middle sr-no-width">#</th>
                                <th className="p-2 align-middle">Date & time (Local)</th>
                                <th className="p-2 align-middle">Name of report</th>
                                <th className="p-2 align-middle">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {page?.length > 0 ? (
                                page?.map((file: any, index: number) => (
                                    <tr key={index}>
                                        <td className="p-2 align-middle sr-no-width">{index + 1}</td>
                                        <td className="p-2 align-middle" style={{ width: '20%' }}>
                                            {dateTimeFormat(file.reporting_date, false, false, true)}
                                        </td>
                                        <td className='p-2 align-middle' style={{ width: '65%' }}>{file.name_of_report}</td>
                                        <td className="p-2 align-middle text-center">
                                            <Button
                                                type="button"
                                                className='btn-sm'
                                                color="primary"
                                                onClick={() => handleUploadButtonClick(file)}
                                            >
                                                Upload
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="p-2 align-middle text-center">
                                        <Message message={'No files are pending for upload.'} type="info" width="100%" />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={5} className="p-2">
                                    <PageNavButton
                                        setPageUrl={setPageUrl}
                                        pageobj={displayData}
                                        pageUrl={pageUrl}
                                        isLoading={fileUploadingLoading}
                                    />
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </CardBody>
            <Modal
                size='md'
                isOpen={modalState}
                toggle={() => tog_backdrop()}
                backdrop={"static"}
                id="staticBackdrop"
            >
                <ModalHeader className='p-2'>Upload Pending BL File
                    <button
                        onClick={async () => {
                            setModalState(false);
                            setBLFiles([]);
                            setEmptyFileError(false);
                        }}
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </ModalHeader>
                <ModalBody className='p-2'>
                    <ReportingFileUpload
                        setFile={setBLFiles}
                        file={BLFiles}
                        index={0}
                        sm={5}
                        CustomAcceptableFileTypes={{
                            'application/pdf': ['.pdf']
                        }}
                    />
                    {emptyFileError &&
                        <Label className="error margin-ve-0-7">Please select a file to upload.</Label>
                    }
                    <div className="mt-1 mb-5">
                        <Button
                            type="submit"
                            color='primary'
                            className='pos-end'
                            disabled={uploading || !BLFiles[0]?.file}
                            onClick={() => handleUpload()}
                        >
                            {uploading && <i className="spinner-border spinner-border-sm text-light me-2" />}
                            Save
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
};

export default PendingBlAttachment;
